import { useState } from 'react';
import useSWR from 'swr';
import api from '@/lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Category {
  id: string;
  nombre: string;
  slug: string;
  descripcion: string | null;
  ocr_keywords: string[];
  color: string;
  activo: number;
  doc_count: number;
}

// ── Fetcher ───────────────────────────────────────────────────────────────────

const fetcher = (url: string) => api.get(url).then(r => r.data);

// ── Color picker presets ──────────────────────────────────────────────────────

const COLOR_PRESETS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b',
  '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6',
];

// ── Modal Formulario ──────────────────────────────────────────────────────────

interface FormData {
  nombre: string;
  slug: string;
  descripcion: string;
  color: string;
  keywords_raw: string; // keywords separadas por coma
}

const EMPTY_FORM: FormData = { nombre: '', slug: '', descripcion: '', color: '#6366f1', keywords_raw: '' };

function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

interface CategoryModalProps {
  initial?: Category | null;
  onSave: (data: Omit<FormData, 'keywords_raw'> & { ocr_keywords: string[] }) => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

function CategoryModal({ initial, onSave, onClose, saving }: CategoryModalProps) {
  const [form, setForm] = useState<FormData>(
    initial
      ? {
          nombre:       initial.nombre,
          slug:         initial.slug,
          descripcion:  initial.descripcion ?? '',
          color:        initial.color,
          keywords_raw: (initial.ocr_keywords ?? []).join(', '),
        }
      : EMPTY_FORM
  );

  const set = (field: keyof FormData, value: string) =>
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'nombre' && !initial) next.slug = slugify(value);
      return next;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ocr_keywords = form.keywords_raw
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
    await onSave({ nombre: form.nombre, slug: form.slug, descripcion: form.descripcion, color: form.color, ocr_keywords });
  };

  const input = (label: string, field: keyof FormData, placeholder?: string) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-muted)', marginBottom: 5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </label>
      <input
        value={form[field]}
        onChange={e => set(field, e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 14,
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          color: 'var(--color-text)', outline: 'none',
        }}
      />
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        style={{
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 12, padding: 32, width: 480, maxWidth: '95vw',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24, color: 'var(--color-text)' }}>
          {initial ? 'Editar categoría' : 'Nueva categoría'}
        </h3>

        {input('Nombre', 'nombre', 'Ej: Facturas')}
        {input('Slug (identificador)', 'slug', 'ej: facturas')}
        {input('Descripción (opcional)', 'descripcion', 'Descripción breve')}

        {/* Keywords OCR */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-muted)', marginBottom: 5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Keywords OCR (separadas por coma)
          </label>
          <textarea
            value={form.keywords_raw}
            onChange={e => set('keywords_raw', e.target.value)}
            placeholder="factura, invoice, total, IVA..."
            rows={3}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 13,
              background: 'var(--color-bg)', border: '1px solid var(--color-border)',
              color: 'var(--color-text)', outline: 'none', resize: 'vertical',
              fontFamily: 'system-ui, sans-serif',
            }}
          />
          <p style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 4 }}>
            El OCR asignará automáticamente esta categoría si detecta estas palabras en el documento.
          </p>
        </div>

        {/* Color */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-muted)', marginBottom: 8, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Color
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {COLOR_PRESETS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => set('color', c)}
                style={{
                  width: 28, height: 28, borderRadius: '50%', background: c,
                  border: form.color === c ? '2px solid #fff' : '2px solid transparent',
                  cursor: 'pointer', flexShrink: 0,
                  boxShadow: form.color === c ? `0 0 0 2px ${c}` : 'none',
                }}
              />
            ))}
            <input
              type="color"
              value={form.color}
              onChange={e => set('color', e.target.value)}
              style={{ width: 28, height: 28, padding: 2, borderRadius: '50%', border: '1px solid var(--color-border)', cursor: 'pointer', background: 'transparent' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{
            padding: '8px 20px', borderRadius: 6, fontSize: 14,
            background: 'transparent', border: '1px solid var(--color-border)',
            color: 'var(--color-muted)', cursor: 'pointer',
          }}>
            Cancelar
          </button>
          <button type="submit" disabled={!form.nombre || !form.slug || saving} style={{
            padding: '8px 20px', borderRadius: 6, fontSize: 14, fontWeight: 500,
            background: !form.nombre || !form.slug || saving ? 'var(--color-border)' : 'var(--color-accent)',
            border: 'none', color: '#fff',
            cursor: !form.nombre || !form.slug || saving ? 'not-allowed' : 'pointer',
          }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function CategoriesPage() {
  const { data, mutate } = useSWR<{ data: Category[] }>('/documents/categories', fetcher);
  const categories = data?.data ?? [];
  const [editing, setEditing]   = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving]     = useState(false);

  const handleCreate = async (fd: { nombre: string; slug: string; descripcion: string; color: string; ocr_keywords: string[] }) => {
    setSaving(true);
    try {
      await api.post('/documents/categories', { ...fd });
      setCreating(false);
      mutate();
    } finally { setSaving(false); }
  };

  const handleUpdate = async (fd: { nombre: string; slug: string; descripcion: string; color: string; ocr_keywords: string[] }) => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/documents/categories/${editing.id}`, { ...fd });
      setEditing(null);
      mutate();
    } finally { setSaving(false); }
  };

  const handleToggle = async (cat: Category) => {
    await api.patch(`/documents/categories/${cat.id}`, { activo: cat.activo ? 0 : 1 });
    mutate();
  };

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
            Categorías documentales
          </h1>
          <p style={{ fontSize: 14, color: 'var(--color-muted)' }}>
            Configura las categorías y sus keywords para clasificación automática OCR
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          style={{
            padding: '8px 20px', borderRadius: 6, fontSize: 14, fontWeight: 500,
            background: 'var(--color-accent)', border: 'none', color: '#fff', cursor: 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          + Nueva categoría
        </button>
      </div>

      {/* Grid de tarjetas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {categories.map(cat => (
          <div
            key={cat.id}
            style={{
              background: 'var(--color-surface)', border: `1px solid var(--color-border)`,
              borderRadius: 10, padding: 20, opacity: cat.activo ? 1 : 0.5,
              borderTop: `3px solid ${cat.color}`,
            }}
          >
            {/* Header tarjeta */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)', flex: 1 }}>{cat.nombre}</span>
              <span style={{
                background: `${cat.color}22`, color: cat.color,
                borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 600,
              }}>
                {cat.doc_count} docs
              </span>
            </div>

            {/* Slug */}
            <p style={{ fontSize: 11, color: 'var(--color-muted)', marginBottom: 8, fontFamily: 'monospace' }}>
              /{cat.slug}
            </p>

            {/* Descripción */}
            {cat.descripcion && (
              <p style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12, lineHeight: 1.5 }}>
                {cat.descripcion}
              </p>
            )}

            {/* Keywords */}
            {(cat.ocr_keywords ?? []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
                {cat.ocr_keywords.slice(0, 6).map(kw => (
                  <span key={kw} style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11,
                    background: 'rgba(255,255,255,0.05)', color: 'var(--color-muted)',
                    border: '1px solid var(--color-border)',
                  }}>
                    {kw}
                  </span>
                ))}
                {cat.ocr_keywords.length > 6 && (
                  <span style={{ fontSize: 11, color: 'var(--color-muted)', padding: '2px 4px' }}>
                    +{cat.ocr_keywords.length - 6} más
                  </span>
                )}
              </div>
            )}

            {/* Acciones */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setEditing(cat)}
                style={{
                  flex: 1, padding: '6px 0', borderRadius: 5, fontSize: 12, fontWeight: 500,
                  background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.25)',
                  color: 'var(--color-accent)', cursor: 'pointer',
                }}
              >
                Editar
              </button>
              <button
                onClick={() => handleToggle(cat)}
                style={{
                  flex: 1, padding: '6px 0', borderRadius: 5, fontSize: 12, fontWeight: 500,
                  background: cat.activo ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                  border: `1px solid ${cat.activo ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)'}`,
                  color: cat.activo ? 'var(--color-danger)' : 'var(--color-success)',
                  cursor: 'pointer',
                }}
              >
                {cat.activo ? 'Desactivar' : 'Activar'}
              </button>
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <p style={{ color: 'var(--color-muted)', fontSize: 14, gridColumn: '1/-1' }}>
            No hay categorías. Crea la primera.
          </p>
        )}
      </div>

      {creating && (
        <CategoryModal onSave={handleCreate} onClose={() => setCreating(false)} saving={saving} />
      )}
      {editing && (
        <CategoryModal initial={editing} onSave={handleUpdate} onClose={() => setEditing(null)} saving={saving} />
      )}
    </div>
  );
}
